import type { ClientEntity } from '../types';

/**
 * ============================================================================
 * O PONTO DE PARTIDA DO EDITOR VISUAL
 * ============================================================================
 *
 * POR QUE ISTO EXISTE
 *
 * O editor abria com o canvas totalmente branco, e a aba "Cores" listava
 * exatamente UMA cor: `#000000`. As duas coisas sao o mesmo sintoma — um
 * documento vazio. `#000000` e a cor de texto herdada de um `<body>` sem nada
 * dentro; nao havia site nenhum para pintar.
 *
 * A causa e simples e nao era erro de codigo: o cliente nao tinha site. O
 * `htmlContent` de `ClientEntity` vem do artefato publicado no R2, e cliente
 * criado pelo CRM (ou importado) nasce sem `siteId`. Sem site publicado nao ha
 * HTML, e o editor mostrava fielmente esse nada — sem uma linha explicando que
 * era isso, o que fazia parecer que o editor estava quebrado.
 *
 * Pior: com o canvas vazio, "Publicacao Final" gravava `<html><head></head>
 * <body></body></html>` como primeira versao do site.
 *
 * A CORRECAO
 *
 * Quem nao tem site abre num RASCUNHO montado com o que ja se sabe do negocio —
 * nome, telefone, endereco, categoria, resumo. Da para editar cor, texto e
 * imagem no mesmo instante, e "Publicacao Final" publica algo que existe.
 *
 * Nao e um gerador de site: e um ponto de partida honesto. Site de verdade sai
 * da fabrica (`criador-sites`), com spec tipado.
 */

const CINZA = '#0f172a';

function escapar(texto: string): string {
  return texto
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/** Digitos do telefone, para montar o link do WhatsApp. */
function apenasDigitos(telefone: string): string {
  return telefone.replace(/\D/g, '');
}

/**
 * Uma pagina inicial editavel a partir dos dados do cliente.
 *
 * Tudo aqui e HTML e CSS embutidos, sem `<script>` e sem recurso externo, por
 * dois motivos: o iframe do editor roda com `sandbox`, e o artefato publicado
 * vai para o R2 e e servido pela borda — quanto menos ele depender de terceiro,
 * menos chance de a primeira versao de um cliente nascer quebrada.
 */
export function htmlInicialDoCliente(client: ClientEntity): string {
  const d = client.data ?? ({} as ClientEntity['data']);
  const nome = escapar(d.name || client.slug);
  const categoria = escapar(d.categories?.[0] ?? 'Atendimento');
  const resumo = escapar(
    d.summary ||
      `${d.name || client.slug} atende com atencao aos detalhes. ` +
        'Troque este texto pelo que voce quer dizer a quem chega aqui.',
  );
  const telefone = escapar(d.phoneNumber ?? '');
  const endereco = escapar(d.address ?? '');
  const zap = apenasDigitos(d.phoneNumber ?? '');

  const contatos = [
    telefone ? `<p class="contato"><strong>Telefone</strong> ${telefone}</p>` : '',
    endereco ? `<p class="contato"><strong>Endereco</strong> ${endereco}</p>` : '',
  ]
    .filter(Boolean)
    .join('\n      ');

  const cta = zap
    ? `<a class="botao" href="https://wa.me/55${zap}" target="_blank" rel="noreferrer">Falar no WhatsApp</a>`
    : `<a class="botao" href="#contato">Entre em contato</a>`;

  return `<!DOCTYPE html>
<html lang="pt-BR">
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
<title>${nome}</title>
<style>
  * { box-sizing: border-box; }
  body { margin: 0; font-family: system-ui, -apple-system, "Segoe UI", sans-serif; color: ${CINZA}; background: #ffffff; }
  .topo { background: ${CINZA}; color: #ffffff; padding: 96px 24px; text-align: center; }
  .topo .marca { font-size: 12px; letter-spacing: .18em; text-transform: uppercase; opacity: .7; margin: 0 0 16px; }
  .topo h1 { font-size: 44px; line-height: 1.1; margin: 0 0 16px; }
  .topo p { font-size: 18px; line-height: 1.6; margin: 0 auto; max-width: 620px; opacity: .85; }
  .botao { display: inline-block; margin-top: 32px; background: #ffffff; color: ${CINZA}; padding: 14px 28px; border-radius: 999px; text-decoration: none; font-weight: 600; }
  .secao { max-width: 860px; margin: 0 auto; padding: 72px 24px; }
  .secao h2 { font-size: 28px; margin: 0 0 16px; }
  .secao p { font-size: 17px; line-height: 1.7; color: #475569; margin: 0 0 12px; }
  .contato { font-size: 16px; color: #334155; }
  .contato strong { display: block; font-size: 12px; letter-spacing: .12em; text-transform: uppercase; color: #94a3b8; margin-bottom: 2px; }
  .rodape { background: #f8fafc; text-align: center; padding: 40px 24px; color: #64748b; font-size: 14px; }
  @media (max-width: 640px) { .topo { padding: 64px 20px; } .topo h1 { font-size: 32px; } }
</style>
</head>
<body>
  <header class="topo">
    <p class="marca">${categoria}</p>
    <h1>${nome}</h1>
    <p>${resumo}</p>
    ${cta}
  </header>

  <section class="secao">
    <h2>Sobre</h2>
    <p>${resumo}</p>
  </section>

  <section class="secao" id="contato">
    <h2>Contato</h2>
      ${contatos || '<p class="contato">Adicione aqui o telefone e o endereco.</p>'}
  </section>

  <footer class="rodape">
    <p>${nome}</p>
  </footer>
</body>
</html>`;
}

/**
 * O documento tem conteudo de verdade?
 *
 * Usado como trava antes de publicar. `<html><head></head><body></body></html>`
 * passa em qualquer verificacao de "string nao vazia" e e exatamente o que o
 * editor gravava por cima de um site publicado quando abria em branco.
 */
export function temConteudoVisivel(html: string): boolean {
  if (!html || html.trim().length === 0) return false;

  const corpo = /<body[^>]*>([\s\S]*)<\/body>/i.exec(html)?.[1] ?? html;
  const semScriptsNemEstilos = corpo
    .replace(/<script[\s\S]*?<\/script>/gi, '')
    .replace(/<style[\s\S]*?<\/style>/gi, '')
    .replace(/<!--[\s\S]*?-->/g, '');

  // Texto solto conta; imagem, video e iframe tambem — uma pagina pode ser so
  // uma imagem e ainda assim ser uma pagina.
  if (/<(img|video|iframe|svg|canvas|picture)\b/i.test(semScriptsNemEstilos)) return true;
  return semScriptsNemEstilos.replace(/<[^>]*>/g, '').trim().length > 0;
}
